import { find, isEmpty } from 'lodash';
import { calcCurrentDuration, calcCurrentPrice } from '../../utils/packagesUtils';
import { mergePackageUGCs } from '../../utils/ugc/mergePackageUGCs';
import { UGC_UPDATED } from '../../actions/ugc';
import {
    SELECT_PACKAGE,
    UPDATE_CALCULATOR,
    UPDATE_CUSTOM_EXTRA,
    UPDATE_EXTRA_FAST,
    UPDATE_FEATURE,
    UPDATE_PRICE,
    UPDATE_QUANTITY,
    UPDATE_VO_DATA,
    SELECT_CHECKOUT_PLAN,
    SELECT_RECURRING_OPTION
} from '../../actions/packages';
import { BASIC_PACKAGE_ID, CHECKOUT_PLANS } from '../../utils/packagesUtils/constants';
import {
    getDefaultRecurringOption,
    getDefaultRecurringOptionId
} from '../../utils/recurring';
import formatPackageList, { getNewPrice, calculateVoPackageData } from './utils';

const packagesReducer = ({ packages = {}, general }) => {

    const { recurringOptions } = packages;

    const initialState = {
        packageList: formatPackageList(packages, general),
        selectedPackageId: BASIC_PACKAGE_ID,
        quantity: 1,
        recurringOptions,
        selectedCheckoutPlan: CHECKOUT_PLANS.SINGLE_ORDER,
        selectedRecurringOptionId: getDefaultRecurringOptionId(recurringOptions)
    };

    return (state = initialState, action) => {
        switch (action.type) {
            case UGC_UPDATED:
                return {
                    ...state,
                    packageList: mergePackageUGCs({ packageList: state.packageList, ugc: action.data.ugc })
                };
            case UPDATE_PRICE:
                return {
                    ...state,
                    packageList: state.packageList.map((packageData) => {
                        const newCurrentPrice = calcCurrentPrice(packageData, state.quantity);

                        return {
                            ...packageData,
                            currentPrice: getNewPrice(packageData, newCurrentPrice)
                        };
                    })
                };
            case UPDATE_CALCULATOR:
                return {
                    ...state,
                    packageList: state.packageList.map((packageData) => {
                        const { calculators } = packageData;
                        const { calculatorId, value } = action.data;

                        const newCalculators = calculators.map((calculator) => {
                            if (calculator.id === calculatorId) {
                                return { ...calculator, currentValue: value };
                            } else {
                                return calculator;
                            }
                        });
                        const newPackageData = { ...packageData, calculators: newCalculators };

                        return {
                            ...newPackageData
                        };
                    })
                };
            case UPDATE_VO_DATA:
                const { voData } = action.data;

                return {
                    ...state,
                    packageList: state.packageList.map((packageData) => calculateVoPackageData(packageData, voData))
                };
            case UPDATE_EXTRA_FAST:
                return {
                    ...state,
                    packageList: state.packageList.map((packageData) => {
                        if (packageData.id !== action.data.packageId) {
                            return packageData;
                        }

                        const { extraFast } = packageData,
                            newExtraFast = { ...extraFast, currentValue: action.data.value },
                            newPackageData = { ...packageData, extraFast: newExtraFast };

                        return {
                            ...newPackageData,
                            currentPrice: calcCurrentPrice(newPackageData, state.quantity),
                            currentDuration: calcCurrentDuration(newPackageData)
                        };
                    })
                };
            case SELECT_PACKAGE:
                return {
                    ...state,
                    selectedPackageId: action.data.packageId
                };
            case UPDATE_FEATURE:
                return {
                    ...state,
                    packageList: state.packageList.map((packageData) => {
                        const { features } = packageData;

                        const newFeatures = features.map((feature) => {
                            if (feature.id !== action.data.featureId) {
                                return feature;
                            } else {
                                const amount = action.data.amount;
                                const currentDuration = amount > 0 ? feature.duration * amount : feature.duration;
                                return { ...feature, amount, currentDuration };
                            }
                        });
                        const newPackageData = { ...packageData, features: newFeatures };

                        return {
                            ...newPackageData,
                            currentPrice: calcCurrentPrice(newPackageData, state.quantity),
                            currentDuration: calcCurrentDuration(newPackageData)
                        };
                    })
                };
            case UPDATE_CUSTOM_EXTRA:
                return {
                    ...state,
                    packageList: state.packageList.map((packageData) => {
                        const { customExtras } = packageData;

                        const newCustomExtras = customExtras.map((customExtra) => {
                            if (customExtra.id !== action.data.customExtraId) {
                                return customExtra;
                            } else {
                                return { ...customExtra, amount: action.data.amount };
                            }
                        });
                        const newPackageData = { ...packageData, customExtras: newCustomExtras };

                        return {
                            ...newPackageData,
                            currentPrice: calcCurrentPrice(newPackageData, state.quantity),
                            currentDuration: calcCurrentDuration(newPackageData)
                        };
                    })
                };
            case UPDATE_QUANTITY:
                return {
                    ...state,
                    packageList: state.packageList.map((packageData) => ({
                        ...packageData,
                        currentPrice: calcCurrentPrice(packageData, action.data.quantity)
                    })),
                    quantity: action.data.quantity
                };
            case SELECT_CHECKOUT_PLAN:
                const { selectedCheckoutPlan } = action.data;

                return {
                    ...state,
                    selectedCheckoutPlan
                };
            case SELECT_RECURRING_OPTION:
                const { selectedRecurringOptionId } = action.data;

                return {
                    ...state,
                    selectedRecurringOptionId
                };
            default:
                return state;
        }
    };
};

// Selectors

export const getPackageById = ({ packageList }, packageId) => packageList.find(({ id }) => (id === packageId));

export const isTriplePackage = ({ packageList }) => packageList.length === 3;

export const isSinglePackage = ({ packageList }) => packageList.length === 1;

export const getSingleCalculator = (state, packageId) => {
    const { calculators } = getPackageById(state, packageId);

    return calculators && calculators.length ? calculators[0] : null;
};

export const getRecurringOptions = ({ recurringOptions }) => recurringOptions;

export const getSelectedCheckoutPlan = ({ selectedCheckoutPlan }) => selectedCheckoutPlan;

export const getSelectedRecurringOptionId = ({ selectedRecurringOptionId }) => selectedRecurringOptionId;

export const getSelectedRecurringOption = (packages) => {
    const { recurringOptions, selectedRecurringOptionId } = packages;
    const recurringOption = find(recurringOptions, { id: selectedRecurringOptionId });

    return recurringOption || getDefaultRecurringOption(recurringOptions);
};

export const isRecurringGig = ({ recurringOptions }) => !isEmpty(recurringOptions);

// End of selectors

export default packagesReducer;
